import { useState } from "react";
import { PencilIcon, Trash2Icon } from "lucide-react";

import { WorkExperience } from "./WorkExperience";

import {
  Column,
  ConfirmationDialog,
  Row,
  SheetSectionEmptyText,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { WorkExperienceType } from "@/types";

export const WorkExperiencesView = ({
  highlightKeywords,
  highlightRelevantTerms,
  onDeleteWorkExperience,
  onEditWorkExperience,
  workExperiences,
}: {
  highlightKeywords?: string;
  highlightRelevantTerms?: boolean;
  onDeleteWorkExperience?: (workExperience: WorkExperienceType) => void;
  onEditWorkExperience?: (workExperienceId: number) => void;
  workExperiences: WorkExperienceType[];
}) => {
  if (!workExperiences.length) {
    return (
      <SheetSectionEmptyText text="This candidate hasn't yet filled out their work experience." />
    );
  }

  return (
    <Column className="gap-7">
      {workExperiences.map((workExperience: WorkExperienceType) => {
        return (
          <Row
            className="group/job w-full motion-safe:duration-300 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1"
            key={workExperience.id}
            onDoubleClick={() => {
              onEditWorkExperience?.(workExperience.id);
            }}
          >
            <WorkExperience
              dateActions={
                onEditWorkExperience ? (
                  <WorkExperienceActions
                    onDelete={
                      onDeleteWorkExperience
                        ? () => {
                            onDeleteWorkExperience(workExperience);
                          }
                        : undefined
                    }
                    onEdit={() => {
                      onEditWorkExperience(workExperience.id);
                    }}
                  />
                ) : null
              }
              highlightKeywords={highlightKeywords}
              highlightRelevantTerms={highlightRelevantTerms}
              workExperience={workExperience}
            />
          </Row>
        );
      })}
    </Column>
  );
};

const WorkExperienceActions = ({
  onDelete,
  onEdit,
}: {
  onDelete?: () => void;
  onEdit: () => void;
}) => {
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Row className="items-center gap-1 opacity-0 transition-opacity duration-150 group-hover/job:opacity-100 group-hover/job:delay-200">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="h-6 w-6"
                onClick={onEdit}
                size="icon"
                type="button"
                variant="ghost"
              >
                <PencilIcon
                  className="h-4 w-4 text-slate-400"
                  strokeWidth={1.5}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit Work Experience</TooltipContent>
          </Tooltip>
          {onDelete ? (
            <Button
              className="h-6 w-6"
              onClick={() => {
                setIsDeleteConfirmationOpen(true);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Trash2Icon className="h-4 w-4 text-red-400" />
            </Button>
          ) : null}
        </Row>
      </TooltipProvider>
      <ConfirmationDialog
        confirmationButtonVariant="destructive"
        confirmButtonText="Delete"
        description="This action can't be undone."
        onConfirm={() => {
          onDelete?.();
          setIsDeleteConfirmationOpen(false);
        }}
        onOpenChange={setIsDeleteConfirmationOpen}
        open={isDeleteConfirmationOpen}
        title="Are you sure?"
      />
    </>
  );
};
